// Палитры шаблонов формул: сетка кнопок с отрисованными формулами (лента, окно редактора).
import { memo, useMemo, useState } from 'react';
import { renderTex } from './ml';
import { tplStatic, type Tpl, type TplGroup, STRUCTS, SYMBOLS, READY, CHEM, UNITS, unitLatex } from './templates';
import { chemToLatex } from './latex';

const cache = new Map<string, string>();
/** Отрисовка шаблона с кешем (палитры перерисовываются часто). */
export function tplHtml(t: string, display = false): string {
  const key = (display ? 'D' : 'I') + t;
  let h = cache.get(key);
  if (h === undefined) { h = renderTex(tplStatic(t), display); cache.set(key, h); }
  return h;
}

export const TplGrid = memo(function TplGrid({ items, cols, onPick, testid }: { items: Tpl[]; cols?: number; onPick: (t: string) => void; testid?: string }) {
  return (
    <div className="mx-grid" style={{ gridTemplateColumns: `repeat(${cols || 6}, minmax(0, 1fr))` }} data-testid={testid}>
      {items.map((it, i) => (
        <button key={i} type="button" className={'mx-tpl' + (it.wide ? ' is-wide' : '')} title={it.title || it.t}
          style={it.wide ? { gridColumn: `span ${Math.min(cols || 6, 3)}` } : undefined}
          onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(it.t)} data-tpl={it.t}>
          <span className="mx-tpl-math" dangerouslySetInnerHTML={{ __html: tplHtml(it.t) }} />
        </button>
      ))}
    </div>
  );
});

export function TplGroups({ groups, onPick }: { groups: TplGroup[]; onPick: (t: string) => void }) {
  return (
    <>
      {groups.map((g) => (
        <section key={g.id} className="mx-pal-sec">
          <div className="mx-pal-h">{g.title}{g.hint && <span className="muted"> · {g.hint}</span>}</div>
          <TplGrid items={g.items} cols={g.cols || 6} onPick={onPick} />
        </section>
      ))}
    </>
  );
}

/** Химическая запись: «H2SO4 + 2NaOH -> Na2SO4 + 2H2O» -> формула (4.6.3). */
export function ChemInput({ onPick }: { onPick: (latex: string) => void }) {
  const [s, setS] = useState('');
  const latex = useMemo(() => (s.trim() ? chemToLatex(s) : ''), [s]);
  return (
    <div className="mx-chem">
      <div className="mx-pal-h">Химическая запись <span className="muted">· символы элементов и индексы прямым шрифтом (4.6.3)</span></div>
      <div className="mx-chem-row">
        <input className="mono" value={s} placeholder="2H2 + O2 -> 2H2O,  Cu^2+,  CuSO4*5H2O,  N2 + 3H2 <=> 2NH3"
          onChange={(e) => setS(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && latex) { e.preventDefault(); onPick(latex); setS(''); } }}
          data-testid="chem-input" />
        <button type="button" className="btn btn-primary" disabled={!latex} onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onPick(latex); setS(''); }}>Вставить</button>
      </div>
      {latex && <div className="mx-chem-prev" dangerouslySetInnerHTML={{ __html: renderTex(latex, true) }} />}
      <div className="muted mx-small">-&gt; реакция, &lt;=&gt; обратимая, ^ газ, v осадок, ^2+ заряд, * кристаллогидрат, -&gt;[t] условие над стрелкой</div>
    </div>
  );
}

export type PalTab = 'struct' | 'sym' | 'ready' | 'chem' | 'units';
export const PAL_TABS: [PalTab, string][] = [['struct', 'Структуры'], ['sym', 'Символы'], ['ready', 'Готовые'], ['chem', 'Химия'], ['units', 'Единицы']];

export function PaletteBody({ tab, onPick }: { tab: PalTab; onPick: (t: string) => void }) {
  if (tab === 'struct') return <TplGroups groups={STRUCTS} onPick={onPick} />;
  if (tab === 'sym') return <TplGroups groups={SYMBOLS} onPick={onPick} />;
  if (tab === 'ready') return <TplGroups groups={READY} onPick={onPick} />;
  if (tab === 'chem') return <><ChemInput onPick={onPick} /><div className="mx-pal-h">Шаблоны</div><TplGrid items={CHEM} cols={4} onPick={onPick} /></>;
  return (
    <section className="mx-pal-sec">
      <div className="mx-pal-h">Единицы величин <span className="muted">· только в строке расчёта с числами (4.6.1.8, 4.6.2.6)</span></div>
      <div className="mx-units">
        {UNITS.map((u) => (
          <button key={u} type="button" className="mx-unit-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(unitLatex(u))}>{u}</button>
        ))}
      </div>
    </section>
  );
}
